import { useCallback } from 'react';

import { Box, IconButton, Tooltip } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import MapPinIcon from '@/components/Icons/MapPinIcon';
import MapPinnedIcon from '@/components/Icons/MapPinnedIcon';
import { MAX_PINNED_IDS } from '@/constants';
import useIsMultipleTeamsEnabled from '@/hooks/useIsMultipleTeamsEnabled';
import useUser from '@/hooks/useUser';
import { WorkspaceSections } from '@/sections/Sidebar/types';
import { objectInsertIf } from '@/utils/collections/insertIf';
import { useSnackbar } from 'notistack';
import { useRecoilState } from 'recoil';

import { pinnedIdsState } from '../atoms';
import { sortPinnedIds } from '../utils';
import { useWorkspaceModelsQuery } from './__generated__/queries.generated';

const POLL_INTERVAL = 15000;

function useModels(poll: boolean = false, teamId?: string) {
  const isMultipleTeamsEnabled = useIsMultipleTeamsEnabled();
  const { data, loading, refetch } = useWorkspaceModelsQuery({
    fetchPolicy: 'cache-first',
    pollInterval: poll ? POLL_INTERVAL : null,
    ...objectInsertIf(teamId, { variables: { teamId } }),
    ...objectInsertIf(isMultipleTeamsEnabled && !teamId, { variables: { all: true } }),
  });

  return { models: data?.models || [], loading: isEmpty(data) && loading, refetch };
}

function usePinnedAPI() {
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [pinnedIds, setPinnedIds] = useRecoilState(
    pinnedIdsState(`${WorkspaceSections.Models}-${user.id}`),
  );
  const { models, loading } = useModels();

  const pinnedModels = models
    .filter((model) => pinnedIds.includes(model.id))
    .sort((a, b) => sortPinnedIds(pinnedIds, a.id, b.id));

  const togglePinEntity = useCallback(
    (entityId: string, modelName: string) => {
      setPinnedIds((prev) => {
        if (prev.includes(entityId)) {
          return prev.filter((id) => id !== entityId);
        }
        enqueueSnackbar(`Pinned ${modelName} to the sidebar`, { variant: 'success' });
        return [entityId, ...prev];
      });
    },
    [setPinnedIds, enqueueSnackbar],
  );

  const canPinMoreModels = pinnedModels.length < MAX_PINNED_IDS;

  function stopPropagation(ev: React.MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  function getPinActionItem(entityId: string, modelName: string) {
    const isPinned = pinnedIds.includes(entityId);
    const isPinDisabled = !canPinMoreModels && !isPinned;

    return (
      <Tooltip
        title={
          isPinDisabled
            ? `Limit ${MAX_PINNED_IDS} pins`
            : isPinned
            ? 'Unpin from sidebar'
            : 'Pin to sidebar'
        }
      >
        <Box>
          <IconButton
            onClick={(e) => {
              stopPropagation(e);
              togglePinEntity(entityId, modelName);
            }}
            data-cy="pin-entity-btn"
            disabled={isPinDisabled}
          >
            {isPinned ? <MapPinnedIcon color="inherit" /> : <MapPinIcon color="secondary" />}
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return { pinnedIds, togglePinEntity, canPinMoreModels, getPinActionItem, pinnedModels, loading };
}

export { useModels, usePinnedAPI };
